import { assertNever } from "@aistihealth/web-common/src/typings/typeUtils"
import Link from "next/link"
import { useRouter } from "next/router"
import React, { FC, ReactNode, useCallback } from "react"
import styled, { css, FlattenSimpleInterpolation } from "styled-components"
import {
  Breakpoint,
  breakpointNames,
  theme,
} from "@aistihealth/web-common/src/util/styles/theme"
import { Box } from "@aistihealth/web-common/src/components/box/Box"

export type Orientation = "vertical" | "horizontal"
export interface ArticleCardProps {
  content: string
  date: string
  href: string
  image: ReactNode
  orientation?: Partial<Record<Breakpoint, Orientation>>
  title: string
}

type ContainerProps = { onClick: () => Promise<void> } & Pick<
  ArticleCardProps,
  "orientation"
>

const horizontalStyle = (): FlattenSimpleInterpolation => {
  return css`
    flex-direction: row;
  `
}

const verticalStyle = (): FlattenSimpleInterpolation => {
  return css`
    flex-direction: column;
  `
}

const generateMediaQueryValues = (
  breakpointName: Breakpoint,
  orientation?: Orientation,
): FlattenSimpleInterpolation => {
  switch (orientation) {
    case "horizontal":
      return css`
        ${theme.breakpoints[breakpointName]} {
          ${horizontalStyle()}
        }
      `
    case "vertical":
      return css`
        ${theme.breakpoints[breakpointName]} {
          ${verticalStyle()}
        }
      `
    case undefined:
      // Return undefined to have the last defined breakpoint (or default) to be
      // used in the later breakpoints
      return undefined
    default:
      return assertNever(orientation)
  }
}

const Container = styled(Box)<ContainerProps>`
  display: flex;
  height: 100%;
  gap: ${theme.spacings.medium};
  justify-content: space-between;

  cursor: pointer;

  // By default horizontal
  ${horizontalStyle()}

  ${({ orientation }) =>
    breakpointNames.map(breakpointName => {
      return generateMediaQueryValues(
        breakpointName,
        orientation[breakpointName],
      )
    })}
`

const ImageContainer = styled(Box)`
  ${theme.breakpoints.sm} {
    flex-basis: 40%;
  }

  padding-top: 0;
  padding-left: 0;
  padding-right: 0;
`

const ContentContainer = styled(Box)`
  flex-basis: 60%;
  padding: 0;
`
const Title = styled.h2`
  margin-top: 0;
  margin-bottom: 0;
  word-break: break-word;
`
const Date = styled.time`
  ${theme.textStyle("default", "medium", "normal")};
  color: ${theme.colors.greyDark};
`
const Content = styled.p``

const TitleLink = styled.a`
  text-decoration: none;
  color: inherit;
`
export const ArticleCard: FC<ArticleCardProps> = ({
  title,
  date,
  image,
  content,
  href,
  orientation = { xs: "vertical" },
}) => {
  const router = useRouter()

  const handleClick = useCallback(async () => {
    await router.push({
      pathname: href,
    })
  }, [href, router])

  return (
    <Container onClick={handleClick} orientation={orientation}>
      <ImageContainer>{image}</ImageContainer>
      <ContentContainer>
        <Title>
          <Link href={href} passHref>
            <TitleLink>{title}</TitleLink>
          </Link>
        </Title>
        <Date>{date}</Date>
        <Content>{content}</Content>
      </ContentContainer>
    </Container>
  )
}
